import { useMemo, useState } from "react";
import { X, Mail, MessageCircle, Instagram, Link, Facebook, Twitter, Ghost } from "lucide-react";
import useEscapeKey from "../hooks/useEscapeKey.tsx";

type ShareModalProps = {
    open: boolean;
    onClose: () => void;
    profileName: string;
    url: string;
    type: string,
}
export default function ShareModal({
    open,
    onClose,
    profileName,
    url,
    type,
}: ShareModalProps) {
    useEscapeKey(open, onClose);
    const [copied, setCopied] = useState(false);

    // Demo-data: bytt ut med faktisk profil-URL og navn fra state/props
    // const profileName = "John Doe";
    const profileUrl = url;

    const shareText = `Sjekk ${type} til ${profileName}: ${profileUrl}`;

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(profileUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
        } catch {
            // Fallback: marker tekst via prompt (enkelt MVP)
            window.prompt("Kopier lenken:", profileUrl);
        }
    }

    function handleEmail() {
        const subject = encodeURIComponent(`Del ${type}: ${profileName}`);
        const body = encodeURIComponent(`${shareText}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }

    function handleSMS() {
        // iOS/Android støtter sms: med body på mange enheter
        const body = encodeURIComponent(shareText);
        window.location.href = `sms:&body=${body}`;
    }

    function openShare(url: string) {
        window.open(url, "_blank", "noopener,noreferrer");
    }

    async function handleSystemShare() {
        if (navigator.share) {
            try {
                await navigator.share({ title: profileName, text: shareText, url: profileUrl });
            } catch {
                // bruker avbrøt – ok
            }
            return;
        }
        await handleCopy();
    }

    return (
        <div className="p-8">
            {/* Overlay */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                        {/* Header */}
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Del {type}</h2>
                            <button onClick={() => onClose()}>
                                <X className="text-gray-500 hover:text-gray-800" />
                            </button>
                        </div>

                        {/* Del-alternativer */}
                        <div className="grid grid-cols-3 gap-4">
                            <ShareItem icon={<MessageCircle />} label="SMS" onClick={handleSMS} />
                            <ShareItem icon={<Mail />} label="E-post" onClick={handleEmail} />
                            <ShareItem
                                icon={<Facebook />}
                                label="Facebook"
                                onClick={() =>
                                    openShare(
                                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`
                                    )
                                }
                            />
                            <ShareItem
                                icon={<Twitter />}
                                label="X"
                                onClick={() =>
                                    openShare(
                                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`
                                    )
                                }
                            />
                            <ShareItem icon={<Ghost />} label="Snapchat" onClick={handleSystemShare} helper="Bruk system-deling" />
                            <ShareItem icon={<Link />} label={copied ? "Kopiert!" : "Kopier lenke"} onClick={handleCopy} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ShareItem({ icon, label, onClick, helper }) {
    return (
        <button
            onClick={onClick}
            className="flex flex-col items-center gap-2 rounded-xl border p-4 hover:bg-gray-50"
            type="button"
            title={helper || label}
            aria-label={label}
        >
            <div className="text-blue-600">{icon}</div>
            <span className="text-sm font-medium">{label}</span>
        </button>
    );
}
